import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { client } from '../api/client';
import { getRefCode } from '../lib/referral';
import { localizedName, localizedDescription } from '../lib/localized';
import ExperienceSelector from '../components/home/ExperienceSelector';

function useCountUp(target: number, duration: number, trigger: boolean) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start: number | null = null;
    let raf = 0;
    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setValue(progress * target);
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, trigger]);
  return value;
}

interface LocalizedProperty {
  id: number;
  status: string;
  name?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  nameZhCn?: string | null;
  imageUrl: string;
  pricePerNight: number;
}

interface LocalizedExperience {
  id: number;
  status: string;
  name?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  nameZhCn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  descriptionZh?: string | null;
  descriptionZhCn?: string | null;
  imageUrl: string;
  duration?: string | null;
}

export default function HomePage() {
  const { t } = useTranslation('home');
  const [properties, setProperties] = useState<LocalizedProperty[]>([]);
  const [experiences, setExperiences] = useState<LocalizedExperience[]>([]);
  const [retreats, setRetreats] = useState<LocalizedExperience[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [propsRes, expRes, retRes] = await Promise.all([
          client.api.fetch('/api/public/properties'),
          client.api.fetch('/api/public/experiences'),
          client.api.fetch('/api/public/retreats'),
        ]);
        const propsJson = await propsRes.json().catch(() => ({ data: [] }));
        const expJson = await expRes.json().catch(() => ({ data: [] }));
        const retJson = await retRes.json().catch(() => ({ data: [] }));
        setProperties(Array.isArray(propsJson.data) ? propsJson.data : []);
        setExperiences(Array.isArray(expJson.data) ? expJson.data : []);
        setRetreats(Array.isArray(retJson.data) ? retJson.data : []);
      } catch (err) {
        console.error('Failed to load homepage data:', err);
      } finally {
        setDataLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.getElementById('hero-bg');
      if (el) {
        el.style.transform = `translateY(${window.scrollY * 0.4}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  async function handleLeadSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLeadError('');
    if (!leadEmail.trim().includes('@')) {
      setLeadError(t('lead.emailInvalid'));
      return;
    }
    try {
      const res = await client.api.fetch('/api/public/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadEmail.trim(),
          lead_type: 'inspiration_guide',
          source: 'homepage',
          referral_code: getRefCode(),
        }),
      });
      if (res.ok) {
        setLeadSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setLeadError(data.error || t('lead.submitFailed'));
      }
    } catch (err) {
      console.error(err);
      setLeadError(t('lead.submitError'));
    }
  }

  const activeProperties = properties.filter((p) => p.status === 'active').slice(0, 3);
  const activeExperiences = experiences.filter((e) => e.status === 'active');
  const activeRetreats = retreats.filter((r) => r.status === 'active');
  const featuredExperiences = [...activeExperiences, ...activeRetreats].slice(0, 3);

  const propertyBadgeColors: Record<number, string> = {
    1: 'bg-red-600',
    2: 'bg-[#B8902F]',
    3: 'bg-[#0a4c6b]',
  };

  const statBookings = useCountUp(1240, 1500, statsVisible);
  const statReturn = useCountUp(38, 1500, statsVisible);
  const statRating = useCountUp(4.9, 1500, statsVisible);

  const testimonials = t('testimonials.stories', { returnObjects: true }) as Array<{
    quote: string;
    name: string;
    title: string;
    unit: string;
    returnRate: string;
  }>;
  const doubled = [...testimonials, ...testimonials];

  return (
    <div>
      {/* Hero Section - Immersive */}
      <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#0d1b2a] md:flex md:items-center md:justify-end">
        {/* Desktop background */}
        <div id="hero-bg" className="absolute inset-0 hidden md:block will-change-transform">
          <img
            src="https://images.unsplash.com/photo-1540202404-a2f29016b523?w=1920&q=80"
            alt="Maldives ocean"
            className="w-full h-full object-cover object-[35%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a4c6b]/50 via-[#0a4c6b]/20 to-[#0d1b2a]/70" />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0d1b2a]/90 via-[#0a4c6b]/30 to-transparent" />
        </div>

        {/* Mobile top image */}
        <div className="relative w-full h-[55vh] md:hidden">
          <img
            src="https://images.unsplash.com/photo-1540202404-a2f29016b523?w=1920&q=80"
            alt="Maldives ocean"
            className="w-full h-full object-cover object-[50%_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a4c6b]/40 via-transparent to-[#0d1b2a]" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center w-full px-6 sm:px-8 md:mx-0 md:ml-auto md:mr-0 md:max-w-xl lg:max-w-2xl md:pl-8 md:pr-12 lg:pr-20 py-12 md:py-0 text-center md:text-right text-white bg-[#0d1b2a] md:bg-transparent">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-lg md:text-xl text-white/80 mb-4 tracking-widest uppercase"
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-snug md:leading-tight"
          >
            {t('hero.title.line1')}<br />{t('hero.title.line2')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto md:mx-0"
          >
            <span className="block text-[#B8902F] font-semibold mb-1">{t('hero.highlight')}</span>
            <span className="block">{t('hero.description')}</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center md:justify-end"
          >
            <button
              onClick={() => window.location.href = '/plan'}
              className="px-8 py-4 bg-[#0a4c6b] text-white font-medium rounded-lg hover:bg-[#0d1b2a] transition-colors text-lg"
            >
              {t('hero.cta.plan')}
            </button>
            <button
              onClick={() => window.location.href = '/experiences'}
              className="px-8 py-4 bg-transparent text-white font-medium rounded-lg border-2 border-white hover:bg-white hover:text-[#0d1b2a] transition-colors text-lg"
            >
              {t('hero.cta.explore')}
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-4 text-sm text-white/70"
          >
            {t('hero.note')}
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 flex flex-col items-center gap-2">
          <span className="text-xs tracking-wider">{t('hero.scrollIndicator')}</span>
          <div className="w-5 h-8 border-2 border-white/50 rounded-full flex items-start justify-center pt-1">
            <div className="w-1 h-2 bg-white/70 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Experience Selector — interactive recommendations below hero */}
      <ExperienceSelector />

      {/* Plan your island holiday — Travel CTA */}
      <section className="bg-[#0a4c6b] py-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('planning.eyebrow')}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('planning.title')}</h2>
            <p className="text-white/70 mb-6">{t('planning.description')}</p>
            <Link
              to="/plan"
              className="inline-block bg-[#B8902F] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#9a7a28] transition"
            >
              {t('planning.cta')}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section ref={statsRef} className="bg-[#0d1b2a] py-4 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-white/90 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[#B8902F] font-bold text-lg">{Math.round(statBookings).toLocaleString()}+</span>
            <span>{t('stats.bookings')}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-white/20" />
          <div className="flex items-center gap-2">
            <span className="text-[#B8902F] font-bold text-lg">{Math.round(statReturn)}%</span>
            <span>{t('stats.returnRate')}</span>
          </div>
          <div className="hidden md:block w-px h-6 bg-white/20" />
          <div className="flex items-center gap-2">
            <span className="text-[#B8902F] font-bold text-lg">{statRating.toFixed(1)}/5</span>
            <span>{t('stats.rating')}</span>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#2ec4b6] font-medium mb-2">{t('about.eyebrow')}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-6">{t('about.title')}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              {t('about.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(t('about.features', { returnObjects: true }) as Array<{ icon: string; title: string; desc: string }>).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 rounded-2xl hover:bg-gray-50 transition"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-semibold text-lg mb-2 text-[#0d1b2a]">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority Endorsement */}
      <section className="py-20 px-4 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#B8902F] font-medium mb-2">{t('team.eyebrow')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a]">{t('team.title')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(t('team.roles', { returnObjects: true }) as Array<{ icon: string; title: string; desc: string }>).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0a4c6b] to-[#2ec4b6] mx-auto mb-4 flex items-center justify-center">
                  <span className="text-white text-2xl">{item.icon}</span>
                </div>
                <h3 className="font-bold text-lg text-[#0d1b2a] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Properties Preview */}
      <section className="py-24 px-4 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2ec4b6] font-medium mb-2">{t('properties.eyebrow')}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-4">{t('properties.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('properties.description')}</p>
          </div>
          {dataLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('properties.loading')}</p>
            </div>
          ) : activeProperties.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('properties.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {activeProperties.map((item, i) => (
                <motion.div
                  key={item.id || i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
                    <img src={item.imageUrl} alt={localizedName(item)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    {propertyBadgeColors[Number(item.id)] && (
                      <span className={`absolute top-3 right-3 ${propertyBadgeColors[Number(item.id)]} text-white text-xs font-bold px-3 py-1 rounded-full z-10`}>
                        {t(`properties.badges.${item.id}`)}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="text-sm text-white/80">{localizedName(item)}</p>
                      <h3 className="text-xl font-bold">{localizedName(item)}</h3>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-2">
                    <span className="text-sm text-gray-500">{t('properties.fromPrice')}</span>
                    <span className="text-lg font-bold text-[#0a4c6b]">{t('properties.price', { price: Number(item.pricePerNight).toLocaleString() })}</span>
                  </div>
                  {t(`properties.taglines.${item.id}`, { defaultValue: '' }) && (
                    <p className="px-2 mt-1 text-sm text-gray-600">{t(`properties.taglines.${item.id}`)}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Link to="/properties" className="inline-flex items-center gap-2 bg-[#0a4c6b] text-white px-8 py-3 rounded-full font-medium hover:bg-[#083d56] transition">
              {t('properties.viewAll')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Reciprocity: Free Inspiration Guide CTA */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0a4c6b] to-[#0d1b2a] rounded-3xl p-8 md:p-12 text-white text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#B8902F]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('guide.eyebrow')}</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('guide.title')}</h2>
              <p className="text-white/80 max-w-2xl mx-auto mb-8">
                {t('guide.description')}
              </p>
              {leadSubmitted ? (
                <p className="text-white font-medium">{t('guide.success')}</p>
              ) : (
                <form onSubmit={handleLeadSubmit} className="flex flex-col sm:flex-row gap-4 justify-center">
                  <input
                    type="email"
                    required
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    placeholder={t('guide.placeholder')}
                    className="px-6 py-3 rounded-full text-gray-900 w-full sm:w-72 text-sm focus:outline-none focus:ring-2 focus:ring-[#B8902F]"
                  />
                  <button type="submit" className="bg-[#B8902F] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#9a7a28] transition">
                    {t('guide.cta')}
                  </button>
                </form>
              )}
              {leadError && <p className="text-red-200 text-xs mt-3">{leadError}</p>}
              <p className="text-white/50 text-xs mt-4">{t('guide.socialProof')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Experiences Preview */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2ec4b6] font-medium mb-2">{t('experiences.eyebrow')}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a] mb-4">{t('experiences.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('experiences.description')}</p>
          </div>
          {dataLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('experiences.loading')}</p>
            </div>
          ) : featuredExperiences.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('experiences.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredExperiences.map((item, i) => (
                <motion.div
                  key={item.id || i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
                    <img src={item.imageUrl} alt={localizedName(item)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="text-sm text-white/80">{localizedName(item)}</p>
                      <h3 className="text-xl font-bold">{localizedName(item)}</h3>
                    </div>
                  </div>
                  <div className="px-2">
                    <p className="text-sm text-[#B8902F] font-medium mb-1">{item.duration}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{localizedDescription(item)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Link to="/experiences" className="inline-flex items-center gap-2 bg-[#0a4c6b] text-white px-8 py-3 rounded-full font-medium hover:bg-[#083d56] transition">
              {t('experiences.viewAll')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-4 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2ec4b6] font-medium mb-2">{t('services.eyebrow')}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0d1b2a]">{t('services.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(t('services.items', { returnObjects: true }) as Array<{ icon: string; title: string; desc: string }>).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl border border-gray-100 hover:border-[#2ec4b6]/30 hover:shadow-lg transition"
              >
                <span className="text-3xl mb-4 block">{item.icon}</span>
                <h3 className="font-semibold text-lg mb-2 text-[#0d1b2a]">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof: Owner Stories */}
      <section className="py-24 px-4 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#B8902F] font-medium mb-2">{t('testimonials.eyebrow')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a]">{t('testimonials.title')}</h2>
          </motion.div>
          <div className="overflow-hidden">
            <div className="testimonial-track">
              {doubled.map((story, i) => (
                <div
                  key={i}
                  className="w-72 mx-4 flex-shrink-0 bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
                >
                  <p className="text-[#0a4c6b] font-medium text-lg mb-6 leading-relaxed">「{story.quote}」</p>
                  <div className="border-t border-gray-100 pt-6">
                    <p className="font-bold text-[#0d1b2a]">{story.name}</p>
                    <p className="text-sm text-gray-500">{story.title}</p>
                    <p className="text-sm text-[#B8902F] font-medium mt-2">{story.unit}</p>
                    <p className="text-xs text-gray-400 mt-1">{story.returnRate}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scarcity Banner */}
      <section className="py-6 px-4 bg-[#B8902F]">
        <div className="max-w-6xl mx-auto text-center text-white">
          <p className="text-sm md:text-base font-medium">
            {t('scarcity.prefix')}<span className="font-bold text-lg">{t('scarcity.highlight')}</span>{t('scarcity.suffix')}
            <Link to="/properties" className="underline ml-1 hover:text-white/80">{t('scarcity.link')}</Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0a4c6b]/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('finalCta.title')}</h2>
          <p className="text-lg text-white/80 mb-8">{t('finalCta.description')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties" className="bg-white text-[#0a4c6b] px-8 py-3 rounded-full font-semibold hover:bg-white/90 transition">
              {t('finalCta.cta')}
            </Link>
          </div>

          {/* Travel agency license */}
          <div className="mt-12 flex flex-col items-center">
            <img
              src="/happy-day-logo.png"
              alt={t('finalCta.agency.alt')}
              className="h-12 w-auto mb-3"
            />
            <p className="text-white/70 text-sm">{t('finalCta.agency.arrangedBy')}</p>
            <p className="text-white/50 text-xs mt-1">{t('finalCta.agency.license')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
